<script setup lang="ts">
import { onMounted, ref } from "vue";

import WIDGET_ANALYTICS_EVENTS from "@/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import { useAnalytics } from "@/hooks/useAnalytics/useAnalytics";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useSuccessfulEvaluationStore } from "@/stores/successfulEvaluation/successfulEvaluation";
import { useTIWStore } from "@/stores/tiw/tiw";
import { useVerificationStore } from "@/stores/verification/verification";
import Button from "@/ui/button/button.vue";
import Checkbox from "@/ui/checkbox/checkbox.vue";

import Modal from "../../modal/modal.vue";

const TIWStore = useTIWStore();

const { verificationData } = useVerificationStore();
const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();

const confirmAccuracy = ref<boolean>(false);

function onConfirmedInfo() {
    SuccessfulEvaluationStore.individualFields.PESEL = verificationData.pesel;
    SuccessfulEvaluationStore.individualFields.dateOfIssue = verificationData.dateOfIssue;
    SuccessfulEvaluationStore.individualFields.idNumber = verificationData.id;
    SuccessfulEvaluationStore.individualFields.name = verificationData.name;
    SuccessfulEvaluationStore.individualFields.surname = verificationData.lastName;

    SuccessfulEvaluationStore.companyFields.identificationNumber = verificationData.pesel;
    SuccessfulEvaluationStore.companyFields.passportNumber = verificationData.id;
    SuccessfulEvaluationStore.companyFields.name = verificationData.name;
    SuccessfulEvaluationStore.companyFields.surname = verificationData.lastName;

    TIWStore.switchStep("successfulEvaluation");
}

function onRepeat() {
    TIWStore.switchStep("documentsUpload");
}

onMounted(() => {
    useAnalytics(WIDGET_ANALYTICS_EVENTS.BIOMETRIC_VERIFICATION_SUCCESS);
});
</script>

<template>
    <Modal class="tiw__step__personAuthentication">
        <div class="tiw__step__personAuthentication__body">
            <svg
                width="64"
                height="64"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M47 6.01404C42.4417 3.37621 37.2666 1.99136 32 2.00004C15.431 2.00004 2 15.431 2 32C2 48.569 15.431 62 32 62C48.569 62 62 48.569 62 32C61.996 29.94 61.796 27.94 61.4 26"
                    stroke="#808D9A"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M20 33.5C20 33.5 24.5 33.5 30.5 44C30.5 44 47.177 16.499 62 11"
                    stroke="#00C853"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
            <div class="tiw__step__personAuthentication__body__info">
                <p class="tiw__step__personAuthentication__body__info__title">
                    {{ useTranslate("tiw__personAuthentication__success__title") }}
                </p>
                <p class="tiw__step__personAuthentication__body__info__text">
                    {{ useTranslate("tiw__personAuthentication__success__text") }}
                </p>
            </div>
            <div class="tiw__step__personAuthentication__body__scanResult">
                <div class="tiw__step__personAuthentication__body__scanResult__main">
                    <img
                        class="tiw__step__personAuthentication__body__scanResult__main__face"
                        :src="`data:image/png;base64, ${verificationData.face}`"
                        alt="face"
                    />
                    <div>
                        <b>{{ verificationData.name }} {{ verificationData.lastName }}</b>
                        <div class="tiw__step__personAuthentication__body__scanResult__main__age">
                            {{
                                verificationData.sex === "M"
                                    ? useTranslate("tiw__male")
                                    : useTranslate("tiw__female")
                            }}
                            <span
                                class="tiw__step__personAuthentication__body__scanResult__main__dot"
                                >•</span
                            >
                            {{ verificationData.age }}
                        </div>
                    </div>
                </div>
                <div class="tiw__step__personAuthentication__body__scanResult__info">
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__dateOfBirth") }}</p>
                        <p>
                            <b>{{ verificationData.dateOfBirth }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__placeOfBirth") }}</p>
                        <p>
                            <b>{{ verificationData.placeOfBirth }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__issuingState") }}</p>
                        <p>
                            <b>{{ verificationData.issuingState }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__nationality") }}</p>
                        <p>
                            <b>{{ verificationData.nationality }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__identityCardNumber") }}</p>
                        <p>
                            <b>{{ verificationData.id }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__pesel") }}</p>
                        <p>
                            <b>{{ verificationData.pesel }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__nationalityCode") }}</p>
                        <p>
                            <b>{{ verificationData.nationalityCode }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__nameOfIssuingAuthority") }}</p>
                        <p>
                            <b>{{ verificationData.nameOfTheIssuingAuthority }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__forms__dateOfIssue") }}</p>
                        <p>
                            <b>{{ verificationData.dateOfIssue }}</b>
                        </p>
                    </div>
                    <div class="tiw__step__personAuthentication__body__scanResult__info__item">
                        <p>{{ useTranslate("tiw__dateOfExpiry") }}</p>
                        <p>
                            <b>{{ verificationData.dateOfExpiry }}</b>
                        </p>
                    </div>
                </div>
                <div class="tiw__step__personAuthentication__body__scanResult__images">
                    <img
                        class="tiw__step__personAuthentication__body__scanResult__info__signature"
                        :src="`data:image/png;base64, ${verificationData.signature}`"
                        alt="signature"
                    />
                    <img
                        v-if="verificationData.card"
                        width="100%"
                        :src="`data:image/png;base64, ${verificationData.card}`"
                        alt="signature"
                    />
                    <img
                        v-if="verificationData.cardBack"
                        width="100%"
                        :src="`data:image/png;base64, ${verificationData.cardBack}`"
                        alt="signature"
                    />
                </div>
            </div>
            <div class="tiw__step__personAuthentication__body__actions">
                <Checkbox v-model="confirmAccuracy" :checked="confirmAccuracy">
                    {{ useTranslate("tiw__person__upload__confirmAccuracy") }}
                </Checkbox>
                <Button
                    :disabled="!confirmAccuracy"
                    class="tiw__step__personAuthentication__body__actions__success"
                    @click="onConfirmedInfo"
                >
                    {{ useTranslate("tiw__continue") }}
                </Button>
                <Button color="light" @click="onRepeat">
                    {{ useTranslate("tiw__person__repeatButton") }}
                </Button>
            </div>
        </div>
    </Modal>
</template>

<style lang="scss" src="./personAuthentication.scss"></style>
